import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { IframeDialogComponent, TrackByItem } from '@components';
import { ProductUid, UserRoleUid } from '@constants';
import { AuthedDirective } from '@directives';
import { AuthService, BreakpointService, UserService } from '@services';
import { firstValueFrom, map, tap } from 'rxjs';

export interface MegaMenuItem {
    label: string;
    routerLink?: string;
    href?: string;
    fragment?: string;
    authed?: boolean;
    portalType?: UserRoleUid.CONSULTANT | UserRoleUid.PARTNER | null;
    loadsInframe?: boolean;
    productUid?: ProductUid[] | null;
}

export interface MegaMenuSection {
    title: string;
    type?: 'spotlight';
    items?: MegaMenuItem[];
    content?: {
        image: string;
        title: string;
        description: string;
        buttonText: string;
        buttonLink?: string;
        buttonAction?: string;
    };
}

@Component({
    selector: 'app-mega-menu',
    imports: [CommonModule, MatIconModule, RouterModule, AuthedDirective],
    templateUrl: './app-mega-menu.component.html',
    styleUrls: ['./app-mega-menu.component.scss'],
})
export class AppMegaMenuComponent implements TrackByItem<any> {
    private userService = inject(UserService);
    private authService = inject(AuthService);
    private dialog = inject(MatDialog);
    private bo = inject(BreakpointObserver);

    @Input() isVisible: boolean = false;
    @Input() title: string = '';
    @Input() menuSections: MegaMenuSection[] = [];
    @Input() queryParams: any = {};
    @Output() menuMouseenter = new EventEmitter<void>();
    @Output() menuMouseleave = new EventEmitter<void>();

    isAuthed$ = this.userService.isAuthenticated$;

    portalAccess$ = this.userService.userRole$.pipe(
        tap((userRole) => console.debug('Current User Role:', userRole)),
        map((userRole) => ({
            showConsultantPortal: userRole === UserRoleUid.CONSULTANT,
            showPartnerPortal: userRole === UserRoleUid.PARTNER,
        })),
    );

    isAuthed = false;

    // ngOnInit(): void {
    //     // Component receives menuSections as input, no need to set selectedProduct$
    // }

    /**
     * Handle on mouse enter event
     */
    onMouseEnter(): void {
        this.menuMouseenter.emit();
    }

    /**
     * Handle on mouse leave event
     */
    onMouseLeave(): void {
        this.menuMouseleave.emit();
    }

    /**
     * Should Show Portal menu items
     * @param item - Mega menu item
     * @param access - check if is Consultant or a Partner has access
     * @returns - boolean to show portal menu item
     */
    shouldShowPortal(
        item: MegaMenuItem,
        access: { showConsultantPortal: boolean; showPartnerPortal: boolean },
    ): boolean {
        if (!item.portalType) return true;
        return item.portalType === UserRoleUid.CONSULTANT ? access.showConsultantPortal : access.showPartnerPortal;
    }

    /**
     * Open MUI Modal Dialog with mega menu link
     * @param item - mega menu item
     */
    handleOpenDialog(item: MegaMenuItem): void {
        firstValueFrom(this.bo.observe('(max-width: 768px)').pipe(map((state) => state.matches)))
            .then((isMobile) => {
                this.onMouseLeave();
                return this.dialog.open(IframeDialogComponent, {
                    width: isMobile ? '' : '80%',
                    maxWidth: BreakpointService.contentMaxWidth,
                    height: isMobile ? '90%' : '80%',
                    data: {
                        title: item?.label,
                        src: item?.href,
                        dataAnalytics: 'header-mega-menu',
                    },
                });
            })
            .catch((e) => {
                throw e;
            });
    }

    /*
     * Track by Item
     * @param _index - item index
     * @param item - any data
     * @returns number or string unique identifier
     */
    trackByItem(index: number, _data: any): NonNullable<number | string> {
        return index;
    }

    /**
     * Handle spotlight button action (e.g. sign-in)
     * @param section - MegaMenuSection
     */
    handleSpotlightAction(section: MegaMenuSection): void {
        this.onMouseLeave();
        if (section.content?.buttonAction === 'signIn') {
            this.authService.signInWithRedirect();
        }
    }
}
